import { useQuery } from '@apollo/client'
import { AccordionContent, AccordionItem, AccordionTrigger } from '@dfh-corp-sites/ui'
import React, { useContext } from 'react'

import { Maybe, ProductProtocolsDocument, ProtocolResource, Slug } from '../../../graphql/api'
import { SimpleProductDetailsFragment } from '../../../graphql/magento'
import useLogAndCaptureError from '../../../hooks/useLogAndCaptureError'
import { TokenContext } from '../../../layouts/context'
import { tokenHasReadProfessionalScope } from '../../../lib/auth/scope'
import { getResourceAssetUrl } from '../../../lib/library'
import { isNotNull } from '../../../utils/collectionTools'

export interface ProtocolsWithProductProps {
  selectedVariant: SimpleProductDetailsFragment | null | undefined
}

type ProtocolResourceLink =
  | Maybe<
      { __typename?: 'ProtocolResource' | undefined } & Pick<ProtocolResource, 'title'> & {
          slug?: Maybe<{ __typename?: 'Slug' | undefined } & Pick<Slug, 'current'>> | undefined
        }
    >
  | undefined

const sortByTitle = (a: ProtocolResourceLink, b: ProtocolResourceLink) => {
  if (a?.title && b?.title) {
    return a.title.localeCompare(b.title)
  }
  // default return value (no sorting)
  return 0
}

export const ProtocolsWithProduct: React.FC<ProtocolsWithProductProps> = ({
  selectedVariant = null,
}) => {
  const token = useContext(TokenContext)
  const { data, error } = useQuery(ProductProtocolsDocument, {
    variables: { sku: selectedVariant?.sku || '' },
    context: {
      token,
      uri: '/api/graphql',
    },
    // skip unless authenticated and user is professional
    skip: !token || !tokenHasReadProfessionalScope(token),
  })
  useLogAndCaptureError(error)

  const protocolLinks = data?.allProduct?.[0]?.protocols
  if (!protocolLinks?.length) return null

  const sortedLinks = protocolLinks?.filter(isNotNull).sort(sortByTitle)
  if (!sortedLinks?.length) return null

  return (
    <AccordionItem
      className="px-2.5 md:px-5 xl:px-10 last:border-b-0 border-brand-rule-grey"
      value="protocol-list"
    >
      <AccordionTrigger iconType="plusMinus" className="text-2xl">
        Protocols With This Product
      </AccordionTrigger>

      <AccordionContent>
        <ul className="list-none pl-5">
          {sortedLinks.map((link, index) => (
            <li className="mb-2 md:mb-4" key={index}>
              <a
                className="text-brand-orange"
                href={getResourceAssetUrl({
                  _type: 'protocol',
                  slug: { current: link?.slug?.current || '' },
                })}
                target="_blank"
                rel="noreferrer"
              >
                {link.title}
              </a>
            </li>
          ))}
        </ul>
      </AccordionContent>
    </AccordionItem>
  )
}
